import { Storage } from '@capacitor/storage';
import { Schedule, Session } from '../models/Schedule';
import { Speaker } from '../models/Speaker';
import { Location } from '../models/Location';
import {
  DATA_LOADED_KEY,
  SCHEDULE_DATA_KEY,
  SPEAKERS_DATA_KEY,
  LOCATIONS_DATA_KEY,
  LAST_UPDATE_KEY,
  EVENTCODE_KEY
} from '../constants/storage';

// Interfaz para el resultado de la carga de datos
export interface DataLoadResult {
  success: boolean;
  hasSchedule: boolean;
  hasSpeakers: boolean;
  hasLocations: boolean;
  errorMessage?: string;
  schedule?: Schedule;
  sessions?: Session[];
  speakers?: Speaker[];
  locations?: Location[];
  allTracks?: string[];
}

export class DataLoadingService {
  private static instance: DataLoadingService;
  private isLoading: boolean = false;
  private loadPromise: Promise<DataLoadResult> | null = null;

  private constructor() {}

  static getInstance(): DataLoadingService {
    if (!DataLoadingService.instance) {
      DataLoadingService.instance = new DataLoadingService();
    }
    return DataLoadingService.instance;
  }

  /**
   * Carga los datos necesarios para la actividad
   * @param eventCode Código del evento a cargar
   * @param forceRefresh Si es true, fuerza la recarga de datos aunque ya estén cargados
   */
  async loadEventData(eventCode: string, forceRefresh: boolean = false): Promise<DataLoadResult> {
    // Si ya hay una carga en progreso, devolver esa promesa
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // Verificar si los datos ya están cargados y son del mismo evento
    if (!forceRefresh) {
      const loadedData = await this.getLoadedData(eventCode);
      if (loadedData) {
        return loadedData;
      }
    }

    // Iniciar nueva carga
    this.isLoading = true;
    this.loadPromise = this._loadEventData(eventCode);

    try {
      const result = await this.loadPromise;
      return result;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Verifica si ya hay datos cargados para el evento
   */
  private async getLoadedData(eventCode: string): Promise<DataLoadResult | null> {
    try {
      // Verificar si hay datos cargados
      const dataLoadedResponse = await Storage.get({ key: DATA_LOADED_KEY });
      const lastEventResponse = await Storage.get({ key: EVENTCODE_KEY });
      
      const dataLoaded = dataLoadedResponse.value === 'true';
      const lastEvent = lastEventResponse.value;
      
      // Si no hay datos cargados o son de otro evento, retornar null
      if (!dataLoaded || lastEvent !== eventCode) {
        return null;
      }
      
      // Obtener los datos almacenados
      const scheduleResponse = await Storage.get({ key: SCHEDULE_DATA_KEY });
      const speakersResponse = await Storage.get({ key: SPEAKERS_DATA_KEY });
      const locationsResponse = await Storage.get({ key: LOCATIONS_DATA_KEY });
      
      // Parsear los datos
      let schedule: Schedule | undefined;
      let sessions: Session[] = [];
      let speakers: Speaker[] = [];
      let locations: Location[] = [];
      let allTracks: string[] = [];
      
      if (scheduleResponse.value) {
        try {
          const scheduleData = JSON.parse(scheduleResponse.value);
          schedule = scheduleData.schedule;
          sessions = scheduleData.sessions || [];
          allTracks = scheduleData.allTracks || [];
        } catch (e) {
          console.warn('Error al parsear datos de agenda:', e);
        }
      }
      
      if (speakersResponse.value) {
        try {
          speakers = JSON.parse(speakersResponse.value) || [];
        } catch (e) {
          console.warn('Error al parsear datos de conferencistas:', e);
        }
      }
      
      if (locationsResponse.value) {
        try {
          locations = JSON.parse(locationsResponse.value) || [];
        } catch (e) {
          console.warn('Error al parsear datos de ubicaciones:', e);
        }
      }
      
      // Retornar el resultado
      return {
        success: true,
        hasSchedule: !!schedule && sessions.length > 0,
        hasSpeakers: speakers.length > 0,
        hasLocations: locations.length > 0,
        schedule,
        sessions,
        speakers,
        locations,
        allTracks
      };
    } catch (error) {
      console.error('Error al verificar datos cargados:', error);
      return null;
    }
  }

  /**
   * Implementación real de la carga de datos
   */
  private async _loadEventData(eventCode: string): Promise<DataLoadResult> {
    try {
      console.log('Cargando datos para el evento:', eventCode);
      
      // URLs de la API
      const dataUrl = `https://esimposio.com/miesimposio/appendpoints/${eventCode}/?type=data&uid=123456`;
      const locationsUrl = `https://esimposio.com/miesimposio/appendpoints/${eventCode}/?type=locations&uid=123456`;
      
      // Realizar las solicitudes a la API
      const responses = await Promise.all([
        fetch(dataUrl),
        fetch(locationsUrl)
      ]);
      
      // Verificar si las respuestas son correctas
      if (!responses[0].ok || !responses[1].ok) {
        throw new Error('Error al obtener datos de la API');
      }
      
      // Procesar los datos
      let schedule: Schedule | undefined;
      let sessions: Session[] = [];
      let speakers: Speaker[] = [];
      let locations: Location[] = [];
      let allTracks: string[] = [];
      
      // Procesar datos principales (agenda y conferencistas)
      try {
        const rawData = await responses[0].text();
        
        // Verificar si la respuesta está vacía
        if (!rawData || rawData.trim() === '') {
          console.warn('La respuesta de la API está vacía');
          throw new Error('Respuesta vacía');
        }
        
        // Intentar parsear el JSON
        const responseData = JSON.parse(rawData);
        
        // Verificar si responseData es null o undefined
        if (!responseData) {
          console.warn('La respuesta de la API es null o undefined');
          throw new Error('Respuesta inválida');
        }
        
        // Procesar agenda
        if (responseData.schedule && Array.isArray(responseData.schedule) && responseData.schedule.length > 0) {
          schedule = this.processSchedule(responseData.schedule[0]);
          sessions = this.parseSessions(schedule);
          
          // Calcular las pistas (tracks) disponibles
          allTracks = sessions
            .reduce((all, session) => {
              if (session.tracks && Array.isArray(session.tracks)) {
                return all.concat(session.tracks);
              }
              return all;
            }, [] as string[])
            .filter((trackName, index, array) => array.indexOf(trackName) === index)
            .sort();
          
          // Guardar datos de agenda
          await Storage.set({
            key: SCHEDULE_DATA_KEY,
            value: JSON.stringify({
              schedule,
              sessions,
              allTracks
            })
          });
        }
        
        // Procesar conferencistas
        if (responseData.speakers && Array.isArray(responseData.speakers)) {
          speakers = responseData.speakers;
          
          // Guardar datos de conferencistas
          await Storage.set({
            key: SPEAKERS_DATA_KEY,
            value: JSON.stringify(speakers)
          });
        }
      } catch (error) {
        console.error('Error al procesar datos principales:', error);
      }
      
      // Procesar ubicaciones
      try {
        const rawLocationsData = await responses[1].text();
        
        // Verificar si la respuesta está vacía
        if (!rawLocationsData || rawLocationsData.trim() === '') {
          console.warn('La respuesta de ubicaciones está vacía');
        } else {
          // Intentar parsear el JSON
          const parsedData = JSON.parse(rawLocationsData);
          
          // Verificar si la respuesta es un objeto con una propiedad 'map'
          if (parsedData && typeof parsedData === 'object' && parsedData.map) {
            if (Array.isArray(parsedData.map)) {
              locations = parsedData.map;
            }
          }
          // Si no hay propiedad map, verificar si la respuesta misma es un array
          else if (Array.isArray(parsedData)) {
            locations = parsedData;
          }
          
          // Guardar datos de ubicaciones
          await Storage.set({
            key: LOCATIONS_DATA_KEY,
            value: JSON.stringify(locations)
          });
        }
      } catch (error) {
        console.warn('Error al procesar ubicaciones:', error);
      }
      
      // Marcar los datos como cargados
      await Storage.set({ key: DATA_LOADED_KEY, value: 'true' });
      await Storage.set({ key: EVENTCODE_KEY, value: eventCode });
      await Storage.set({ key: LAST_UPDATE_KEY, value: new Date().toISOString() });
      
      // Retornar el resultado
      return {
        success: true,
        hasSchedule: !!schedule && sessions.length > 0,
        hasSpeakers: speakers.length > 0,
        hasLocations: locations.length > 0,
        schedule,
        sessions,
        speakers,
        locations,
        allTracks
      };
    } catch (error) {
      console.error('Error al cargar datos del evento:', error);
      
      // Retornar error
      return {
        success: false,
        hasSchedule: false,
        hasSpeakers: false,
        hasLocations: false,
        errorMessage: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  /**
   * Procesa los datos de agenda para asegurar que tengan la estructura correcta
   */
  private processSchedule(scheduleData: any): Schedule {
    try {
      // Verificar si schedule tiene la estructura esperada
      if (!scheduleData.date) {
        scheduleData.date = new Date().toISOString();
      }

      if (!scheduleData.groups) {
        scheduleData.groups = [];
      } else if (!Array.isArray(scheduleData.groups)) {
        // Si groups no es un array, intentar convertirlo
        if (typeof scheduleData.groups === 'object' && scheduleData.groups !== null) {
          scheduleData.groups = [scheduleData.groups];
        } else {
          scheduleData.groups = [];
        }
      }
      
      return scheduleData as Schedule;
    } catch (error) {
      console.error('Error al procesar schedule:', error);
      return { date: new Date().toISOString(), groups: [] };
    }
  }

  /**
   * Parsea las sesiones de la agenda
   */
  private parseSessions(schedule: Schedule): Session[] {
    const sessions: Session[] = [];

    // Verificar si schedule y schedule.groups existen antes de intentar iterar
    if (schedule && schedule.groups && Array.isArray(schedule.groups)) {
      schedule.groups.forEach((g, groupIndex) => {
        try {
          // Verificar si g.sessions existe y es un array
          if (g && g.sessions && Array.isArray(g.sessions)) {
            g.sessions.forEach((s, sessionIndex) => {
              try {
                // Verificar que la sesión tenga las propiedades mínimas necesarias
                if (!s) {
                  return; // Continuar con la siguiente sesión
                }

                // Asegurarse de que la sesión tenga un ID
                if (!s.id) {
                  // Generar un ID si no existe
                  s.id = `generated-${groupIndex}-${sessionIndex}-${Date.now()}`;
                }

                // Asegurarse de que la sesión tenga un nombre
                if (!s.name) {
                  s.name = `Sesión ${sessionIndex + 1}`;
                }

                // Asegurarse de que la sesión tenga tracks
                if (!s.tracks || !Array.isArray(s.tracks) || s.tracks.length === 0) {
                  s.tracks = ['General'];
                }

                // Asegurarse de que la sesión tenga speakerNames
                if (!s.speakerNames || !Array.isArray(s.speakerNames)) {
                  s.speakerNames = [];
                }

                // Asegurarse de que la sesión tenga una fecha válida
                if (!s.date) {
                  s.date = new Date().toISOString().split('T')[0]; // Solo la fecha YYYY-MM-DD
                }

                // Asegurarse de que la sesión tenga horas de inicio y fin
                if (!s.timeStart) {
                  s.timeStart = new Date().toTimeString().split(' ')[0]; // Solo la hora HH:MM:SS
                }

                if (!s.timeEnd) {
                  // Calcular una hora de fin 1 hora después del inicio
                  const startTime = new Date(`2000-01-01T${s.timeStart}`);
                  startTime.setHours(startTime.getHours() + 1);
                  s.timeEnd = startTime.toTimeString().split(' ')[0];
                }

                sessions.push(s);
              } catch (e) {
                console.warn(`Error al procesar sesión ${sessionIndex} en grupo ${groupIndex}:`, e);
              }
            });
          }
        } catch (e) {
          console.warn(`Error al procesar grupo ${groupIndex}:`, e);
        }
      });
    }

    return sessions;
  }

  /**
   * Limpia los datos cargados
   */
  async clearLoadedData(): Promise<void> {
    try {
      await Storage.remove({ key: DATA_LOADED_KEY });
      await Storage.remove({ key: SCHEDULE_DATA_KEY });
      await Storage.remove({ key: SPEAKERS_DATA_KEY });
      await Storage.remove({ key: LOCATIONS_DATA_KEY });
      await Storage.remove({ key: LAST_UPDATE_KEY });
    } catch (error) {
      console.error('Error al limpiar datos cargados:', error);
    }
  }
}

export default DataLoadingService;
